import { useState, useEffect } from 'react';
import { CircleAlert as AlertCircle } from 'lucide-react';
import Modal from '../common/Modal';
import { createOpportunity, listAccounts } from '../../lib/mavisApi';
import { useAuth } from '../../contexts/AuthContext';
import type { OpportunityStage, ForecastLabel, OpportunityMotion, CustomerRelationship } from '../../types/mavis';
import {
  OPPORTUNITY_STAGE_LABELS, FORECAST_LABEL_LABELS,
  OPPORTUNITY_MOTION_LABELS, CUSTOMER_RELATIONSHIP_LABELS,
} from '../../types/mavis';

const STAGES: OpportunityStage[] = [
  'discover', 'qualify', 'validate', 'propose', 'negotiate', 'close_won', 'close_lost',
];
const FORECAST_LABELS: ForecastLabel[] = [
  'low', 'medium', 'high', 'commit_risk', 'commit_ready',
];
const CURRENCIES = ['USD', 'GBP', 'EUR', 'AUD', 'CAD', 'CHF', 'JPY', 'SGD', 'INR', 'HKD', 'NZD'];
const OPPORTUNITY_MOTIONS: OpportunityMotion[] = [
  'new_business_land', 'renewal', 'upsell', 'cross_sell', 'expansion',
  'competitive_displacement', 'partner_led', 'save_risk', 'unknown',
];
const CUSTOMER_RELATIONSHIPS: CustomerRelationship[] = [
  'net_new', 'existing_customer', 'former_customer', 'partner_influenced', 'unknown',
];

type AccountOption = { id: string; company_name: string };

interface FormState {
  accountId: string;
  title: string;
  amount: string;
  currency: string;
  stage: OpportunityStage;
  targetCloseDate: string;
  forecastLabel: string;
  forecastPercentage: string;
  description: string;
  opportunityMotion: OpportunityMotion;
  customerRelationship: CustomerRelationship;
  incumbentVendor: string;
  competitorInPlay: string;
}

function emptyForm(): FormState {
  return {
    accountId: '',
    title: '',
    amount: '',
    currency: 'USD',
    stage: 'discover',
    targetCloseDate: '',
    forecastLabel: '',
    forecastPercentage: '',
    description: '',
    opportunityMotion: 'unknown',
    customerRelationship: 'unknown',
    incumbentVendor: '',
    competitorInPlay: '',
  };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (opportunityId: string) => void;
  /** Pre-select a specific account when opening from an account context */
  defaultAccountId?: string;
  /** Display name for the pre-selected account (shown in locked mode) */
  defaultAccountName?: string;
  /** When true, the account field is shown as read-only and the account list is not loaded */
  lockAccount?: boolean;
}

export default function MavisCreateOpportunityModal({
  isOpen, onClose, onCreated,
  defaultAccountId, defaultAccountName, lockAccount = false,
}: Props) {
  const { organisation, user } = useAuth();
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reset form and optionally load accounts each time the modal opens
  useEffect(() => {
    if (!isOpen || !organisation) return;
    setForm({ ...emptyForm(), accountId: defaultAccountId ?? '' });
    setError('');

    if (!lockAccount) {
      setAccounts([]);
      setLoadingAccounts(true);
      listAccounts(organisation.id)
        .then(({ accounts: data }) => {
          setAccounts(data as AccountOption[]);
        })
        .catch(err => {
          setError(err instanceof Error ? err.message : 'Could not load accounts. Please close and retry.');
        })
        .finally(() => {
          setLoadingAccounts(false);
        });
    }
  }, [isOpen, organisation, defaultAccountId, lockAccount]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    if (!organisation || !user) return;
    if (!form.accountId) { setError('Please select an account.'); return; }
    if (!form.title.trim()) { setError('Opportunity title is required.'); return; }

    setError('');
    setSubmitting(true);

    // The server derives the organisation from the account and sets created_by to the caller.
    const payload: Record<string, unknown> = {
      account_id:       form.accountId,
      title:            form.title.trim(),
      currency:         form.currency,
      stage:            form.stage,
      description:      form.description.trim(),
      forecast_source:  'manual',
      forecast_reasoning: '',
      owner_user_id:    user.id,
    };

    const parsedAmount = parseFloat(form.amount);
    if (form.amount !== '' && !isNaN(parsedAmount)) payload.amount = parsedAmount;

    if (form.targetCloseDate) payload.target_close_date = form.targetCloseDate;

    if (form.forecastLabel) payload.forecast_label = form.forecastLabel;

    payload.opportunity_motion    = form.opportunityMotion;
    payload.customer_relationship = form.customerRelationship;
    if (form.incumbentVendor.trim())   payload.incumbent_vendor   = form.incumbentVendor.trim();
    if (form.competitorInPlay.trim())  payload.competitor_in_play = form.competitorInPlay.trim();

    const parsedPct = parseInt(form.forecastPercentage, 10);
    if (form.forecastPercentage !== '' && !isNaN(parsedPct)) {
      payload.forecast_percentage = Math.min(100, Math.max(0, parsedPct));
    }

    try {
      const { opportunity } = await createOpportunity(payload);
      onCreated(opportunity.id as string);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to create opportunity. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = form.accountId !== '' && form.title.trim() !== '';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="New Opportunity" size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Account */}
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-app-ink mb-1.5">
              Account {!lockAccount && <span className="text-red-500">*</span>}
            </label>
            {lockAccount ? (
              <div className="w-full px-3 py-2.5 bg-app-canvas border border-app-line rounded-lg text-sm text-app-muted cursor-not-allowed">
                {defaultAccountName ?? 'Selected account'}
              </div>
            ) : (
              <select
                value={form.accountId}
                onChange={e => set('accountId', e.target.value)}
                className="input-field"
                disabled={loadingAccounts}
              >
                <option value="">
                  {loadingAccounts ? 'Loading accounts…' : 'Select an account'}
                </option>
                {accounts.map(a => (
                  <option key={a.id} value={a.id}>{a.company_name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Title */}
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-app-ink mb-1.5">
              Opportunity Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={e => set('title', e.target.value)}
              className="input-field"
              placeholder="e.g. Acme Corp — Platform Expansion Q3 2026"
            />
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-app-ink mb-1.5">Amount</label>
            <input
              type="number"
              min="0"
              step="1"
              value={form.amount}
              onChange={e => set('amount', e.target.value)}
              className="input-field"
              placeholder="e.g. 75000"
            />
          </div>

          {/* Currency */}
          <div>
            <label className="block text-sm font-medium text-app-ink mb-1.5">Currency</label>
            <select
              value={form.currency}
              onChange={e => set('currency', e.target.value)}
              className="input-field"
            >
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Stage */}
          <div>
            <label className="block text-sm font-medium text-app-ink mb-1.5">Stage</label>
            <select
              value={form.stage}
              onChange={e => set('stage', e.target.value as OpportunityStage)}
              className="input-field"
            >
              {STAGES.map(s => (
                <option key={s} value={s}>{OPPORTUNITY_STAGE_LABELS[s]}</option>
              ))}
            </select>
          </div>

          {/* Target close date */}
          <div>
            <label className="block text-sm font-medium text-app-ink mb-1.5">Target Close Date</label>
            <input
              type="date"
              value={form.targetCloseDate}
              onChange={e => set('targetCloseDate', e.target.value)}
              className="input-field"
            />
          </div>

          {/* Forecast label */}
          <div>
            <label className="block text-sm font-medium text-app-ink mb-1.5">Forecast Label</label>
            <select
              value={form.forecastLabel}
              onChange={e => set('forecastLabel', e.target.value)}
              className="input-field"
            >
              <option value="">— None —</option>
              {FORECAST_LABELS.map(l => (
                <option key={l} value={l}>{FORECAST_LABEL_LABELS[l]}</option>
              ))}
            </select>
          </div>

          {/* Forecast % */}
          <div>
            <label className="block text-sm font-medium text-app-ink mb-1.5">
              Forecast % <span className="text-xs text-app-muted font-normal">(0–100)</span>
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="5"
              value={form.forecastPercentage}
              onChange={e => set('forecastPercentage', e.target.value)}
              className="input-field"
              placeholder="e.g. 75"
            />
          </div>

          {/* Description */}
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-app-ink mb-1.5">Description</label>
            <textarea
              value={form.description}
              onChange={e => set('description', e.target.value)}
              className="input-field resize-none"
              rows={3}
              placeholder="Optional deal context, background, or notes…"
            />
          </div>
        </div>

        {/* Deal Context section */}
        <div className="border-t border-app-line pt-4">
          <h3 className="text-xs font-semibold text-app-muted uppercase tracking-wider mb-3">
            Deal Context
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-app-ink mb-1.5">Opportunity Motion</label>
              <select
                value={form.opportunityMotion}
                onChange={e => set('opportunityMotion', e.target.value as OpportunityMotion)}
                className="input-field"
              >
                {OPPORTUNITY_MOTIONS.map(m => (
                  <option key={m} value={m}>{OPPORTUNITY_MOTION_LABELS[m]}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-app-ink mb-1.5">Customer Relationship</label>
              <select
                value={form.customerRelationship}
                onChange={e => set('customerRelationship', e.target.value as CustomerRelationship)}
                className="input-field"
              >
                {CUSTOMER_RELATIONSHIPS.map(r => (
                  <option key={r} value={r}>{CUSTOMER_RELATIONSHIP_LABELS[r]}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-app-ink mb-1.5">Incumbent Vendor</label>
              <input
                type="text"
                value={form.incumbentVendor}
                onChange={e => set('incumbentVendor', e.target.value)}
                className="input-field"
                placeholder="e.g. Salesforce"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-app-ink mb-1.5">Competitor in Play</label>
              <input
                type="text"
                value={form.competitorInPlay}
                onChange={e => set('competitorInPlay', e.target.value)}
                className="input-field"
                placeholder="e.g. HubSpot"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-700">{error}</p>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button onClick={onClose} className="btn-ghost flex-1" disabled={submitting}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !isValid}
            className="btn-primary flex-1"
          >
            {submitting ? 'Creating…' : 'Create Opportunity'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
